<template>
    <div>
        <div class="mt-2 border-t border-gray-300 px-2 pt-2 dark:border-gray-600">
            <span class="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Dev</span>
        </div>

        <!-- File type switcher -->
        <div class="px-2 py-1.5">
            <div class="mb-1 text-[11px] text-gray-500 dark:text-gray-400">File type</div>
            <div class="flex gap-1">
                <button
                    v-for="type in fileTypes"
                    :key="type.value"
                    :class="[
                        'rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
                        currentFileType === type.value
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600',
                    ]"
                    @click="switchFileType(type.value)"
                >
                    {{ type.label }}
                </button>
            </div>
        </div>

        <!-- PDF page sets -->
        <div v-if="isPdf" class="px-2 py-1.5">
            <div class="mb-1 text-[11px] text-gray-500 dark:text-gray-400">PDF set</div>
            <div class="flex gap-1">
                <button
                    v-for="set in pdfSets"
                    :key="set.value"
                    :class="[
                        'rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
                        currentPdfSet === set.value
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600',
                    ]"
                    @click="switchPdfSet(set.value)"
                >
                    {{ set.label }}
                </button>
            </div>
        </div>

        <!-- PDF article sets -->
        <div v-if="isPdf" class="px-2 py-1.5">
            <div class="mb-1 text-[11px] text-gray-500 dark:text-gray-400">Article set</div>
            <div class="flex flex-wrap gap-1">
                <button
                    v-for="set in articleSets"
                    :key="set"
                    :class="[
                        'rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
                        currentTestArticles === set
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600',
                    ]"
                    @click="switchTestArticles(set)"
                >
                    {{ set }}
                </button>
            </div>
        </div>

        <!-- EPUB sets -->
        <div v-if="isEpub" class="px-2 py-1.5">
            <div class="mb-1 text-[11px] text-gray-500 dark:text-gray-400">EPUB set</div>
            <div class="flex flex-wrap gap-1">
                <button
                    v-for="set in epubSets"
                    :key="set"
                    :class="[
                        'rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
                        currentEpubSet === set
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600',
                    ]"
                    @click="switchEpubSet(set)"
                >
                    {{ set }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import testEpubFiles from '../../../src/js/TestState/Epub/testEpubFiles.js';

export default {
    data() {
        const params = new URLSearchParams(window.location.search);
        return {
            urlFileType: params.get('file_type') || 'pdf',
            urlEpubSet: params.get('epub_set') || 'simple',
            urlTestArticles: params.get('test_articles') || 'original',
            urlPdfSet: params.get('pdf_set') || 'default',
        };
    },
    computed: {
        currentFileType() {
            return this.urlFileType;
        },
        isPdf() {
            return this.currentFileType === 'pdf';
        },
        isEpub() {
            return this.currentFileType === 'epub';
        },
        currentEpubSet() {
            return this.urlEpubSet;
        },
        currentTestArticles() {
            return this.urlTestArticles;
        },
        currentPdfSet() {
            return this.urlPdfSet;
        },
        pdfSets() {
            return [
                { value: 'default', label: 'Default' },
                { value: 'stress-532', label: 'Stress 532' },
            ];
        },
        fileTypes() {
            return [
                { value: 'pdf', label: 'PDF' },
                { value: 'epub', label: 'EPUB' },
                { value: 'audio', label: 'Audio' },
            ];
        },
        articleSets() {
            return ['original', 'simple', 'magazine', 'educational', 'mixed', 'stress'];
        },
        epubSets() {
            return Object.keys(testEpubFiles);
        },
    },
    methods: {
        updateUrlAndReload(params) {
            const url = new URLSearchParams(window.location.search);
            for (const [key, value] of Object.entries(params)) {
                if (value === null) {
                    url.delete(key);
                } else {
                    url.set(key, value);
                }
            }
            window.location.search = url.toString();
        },
        switchFileType(type) {
            this.updateUrlAndReload({
                file_type: type,
                epub_set: null,
                test_articles: null,
            });
        },
        switchEpubSet(set) {
            this.updateUrlAndReload({ epub_set: set });
        },
        switchTestArticles(set) {
            this.updateUrlAndReload({ test_articles: set });
        },
        switchPdfSet(set) {
            this.updateUrlAndReload({ pdf_set: set === 'default' ? null : set });
        },
    },
};
</script>
